import * as React from "react";
import { Check, Plus } from "lucide-react";

import { cn } from "../../../lib/utils.js";
import { Button } from "../../ui/button.js";
import { Input } from "../../ui/input.js";
import { CATEGORY_STYLE } from "../../calendar/todoStyles.js";
import {
  CATEGORY_LABEL, CATEGORY_ORDER, categoryOf, firstLetter, groupByDate, type CalendarTodo, type TodoCategory,
} from "../../../lib/calendarTodos.js";
import {
  MONTH_MINI, addDays, cleanQuickAddTitle, dayLongLabel, dayTooltip, isoDate, leadingBlanksSun, parseIso, weekTileView,
  startOfWeekSun, type Range,
} from "./my-todos-calendar.js";

export const FOCUS = "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500";
// Static z-index ladder so the first circle sits on top of the next.
const Z = ["z-[20]", "z-[19]", "z-[18]", "z-[17]"];

/** Round tick in the category colour; the fill pops and the check scales in. */
export function TickButton({ todo, onToggle }: { todo: CalendarTodo; onToggle: (id: string, done: boolean) => void }) {
  const cat = categoryOf(todo.tone);
  const s = CATEGORY_STYLE[cat];
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={todo.done}
      aria-label={`${todo.done ? "Mark not done" : "Mark done"}: ${todo.title}`}
      onClick={(e) => { e.stopPropagation(); onToggle(todo.id, !todo.done); }}
      className={cn(
        "flex size-5 shrink-0 items-center justify-center rounded-full border-2 p-0 transition-[background,transform] duration-200 active:scale-90 motion-reduce:transition-none",
        s.check, todo.done && s.checkOn, FOCUS,
      )}
    >
      <Check className={cn("size-3 transition-all duration-200 motion-reduce:transition-none", todo.done ? "scale-100 opacity-100" : "scale-50 opacity-0")} strokeWidth={3} />
    </button>
  );
}

export function CategoryLegend() {
  return (
    <div className="mt-3 flex flex-wrap items-center justify-center gap-x-3.5 gap-y-1 @max-[360px]:hidden">
      {CATEGORY_ORDER.map(c => (
        <span key={c} className="flex items-center gap-1.5 text-[12px] text-surface-500">
          <i className={cn("size-[9px] rounded-full", CATEGORY_STYLE[c].dot)} />{CATEGORY_LABEL[c]}
        </span>
      ))}
    </div>
  );
}

interface ViewProps {
  range: Range;
  todos: CalendarTodo[];
  selected: string;
  todayIso: string;
  onSelect: (d: string) => void;
}

// ─── Week: seven tall tiles with vertically stacked circles ───────────────────
export function WeekStrip({ range, todos, selected, todayIso, onSelect }: ViewProps) {
  const byDate = React.useMemo(() => groupByDate(todos), [todos]);
  const dates = React.useMemo(() => {
    const start = startOfWeekSun(parseIso(range.from));
    return Array.from({ length: 7 }, (_, i) => isoDate(addDays(start, i)));
  }, [range.from]);
  const tiles = React.useMemo(() => dates.map(d => ({ date: d, tile: weekTileView(byDate.get(d) ?? [], d, todayIso) })), [dates, byDate, todayIso]);

  return (
    <div role="group" aria-label="Week" className="grid grid-cols-7 gap-2">
      {tiles.map(({ date, tile }) => {
        const d = parseIso(date);
        const isToday = date === todayIso;
        return (
          <button
            key={date}
            type="button"
            data-day={date}
            tabIndex={date === selected ? 0 : -1}
            aria-pressed={date === selected}
            aria-current={isToday ? "date" : undefined}
            aria-label={`${dayLongLabel(date)}: ${tile.view.total} todo${tile.view.total === 1 ? "" : "s"}, ${tile.view.doneCount} done`}
            title={dayTooltip(date, tile.view.sorted)}
            onClick={() => onSelect(date)}
            className={cn("group flex flex-col items-stretch gap-[5px] rounded-[10px] p-0", FOCUS)}
          >
            <span className={cn(
              "relative flex h-[108px] flex-col items-center justify-center rounded-[10px] border bg-surface-100 [--cb:var(--color-surface-100)] transition-transform duration-150 group-hover:-translate-y-0.5 motion-reduce:transform-none motion-reduce:transition-none @max-[360px]:h-[92px]",
              isToday ? "border-[1.5px] border-brand-500" : "border-surface-200",
              date === selected && "ring-2 ring-brand-600",
            )}>
              <span className="flex flex-col items-center pb-3">
                {tile.circles.map((t, i) => (
                  <span
                    key={t.id}
                    className={cn(
                      "flex size-[22px] items-center justify-center rounded-full text-[10.5px] font-bold leading-none shadow-[0_0_0_2px_var(--cb)]",
                      i > 0 && "-mt-2", Z[i], CATEGORY_STYLE[categoryOf(t.tone)].circle, t.done && "opacity-50",
                    )}
                  >
                    {t.done ? <Check size={12} strokeWidth={3} /> : firstLetter(t.title)}
                  </span>
                ))}
                {tile.plusN > 0 && (
                  <span className="z-[16] -mt-2 flex size-[22px] items-center justify-center rounded-full bg-surface-300 text-[10.5px] font-bold leading-none text-surface-900 shadow-[0_0_0_2px_var(--cb)]">
                    +{tile.plusN}
                  </span>
                )}
              </span>
              <span className={cn(
                "absolute bottom-1.5 text-[10.5px] font-bold",
                tile.line.kind === "late" ? "text-error" : "text-surface-500",
              )}>
                {tile.line.text}
              </span>
            </span>
            <span className={cn("text-center text-[10.5px] font-bold uppercase tracking-[0.05em] text-surface-500 @max-[360px]:text-[9px]", isToday && "text-brand-600")}>
              {d.toLocaleDateString(undefined, { weekday: "short" })}
            </span>
            <span className="text-center text-[14px] text-surface-700 @max-[360px]:text-[11px]">{d.getDate()}</span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Month: compact 7-column grid with up to 3 mini circles ───────────────────
export function MonthGrid({ range, todos, selected, todayIso, weekdays, onSelect }: ViewProps & { weekdays: string[] }) {
  const byDate = React.useMemo(() => groupByDate(todos), [todos]);
  const first = parseIso(range.from);
  const blanks = leadingBlanksSun(first);
  const count = parseIso(range.to).getDate();
  const cells = React.useMemo(() => Array.from({ length: count }, (_, i) => {
    const date = isoDate(addDays(first, i));
    return { date, tile: weekTileView(byDate.get(date) ?? [], date, todayIso) };
  }), [count, first.getTime(), byDate, todayIso]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div role="grid" aria-label="Month">
      <div role="row" className="mb-1 grid grid-cols-7 gap-1 @max-[360px]:gap-[3px]">
        {weekdays.map(w => <span key={w} role="columnheader" className="text-center text-[10.5px] uppercase text-surface-500">{w}</span>)}
      </div>
      {/* 6 rows x 27px + 5 gaps x 3px + the header above = the 196px area exactly. */}
      <div className="grid grid-cols-7 gap-x-1 gap-y-[3px] @max-[360px]:gap-x-[3px]">
        {Array.from({ length: blanks }, (_, i) => <span key={`b${i}`} aria-hidden="true" />)}
        {cells.map(({ date, tile }) => {
          const isToday = date === todayIso;
          const overdue = tile.view.overdue > 0;
          return (
            <button
              key={date}
              type="button"
              role="gridcell"
              data-day={date}
              tabIndex={date === selected ? 0 : -1}
              aria-selected={date === selected}
              aria-current={isToday ? "date" : undefined}
              aria-label={`${dayLongLabel(date)}: ${tile.view.total} todo${tile.view.total === 1 ? "" : "s"}, ${tile.view.doneCount} done`}
              title={dayTooltip(date, tile.view.sorted)}
              onClick={() => onSelect(date)}
              className={cn(
                "flex h-[27px] items-center justify-between rounded-[7px] border px-1.5 text-[11px] transition-transform duration-100 hover:scale-[1.06] motion-reduce:transform-none motion-reduce:transition-none @max-[360px]:px-1",
                date === selected ? "bg-brand-100 [--cb:var(--color-brand-100)] ring-2 ring-brand-600" : "bg-surface-100 [--cb:var(--color-surface-100)]",
                isToday ? "border-[1.5px] border-brand-500" : "border-transparent",
                FOCUS,
              )}
            >
              <span className={cn("font-semibold", overdue ? "text-error" : "text-surface-700")}>{parseIso(date).getDate()}</span>
              <span className="flex items-center">
                {tile.view.sorted.slice(0, MONTH_MINI).map((t, i) => (
                  <span
                    key={t.id}
                    className={cn(
                      "size-2.5 rounded-full shadow-[0_0_0_1.5px_var(--cb)] @max-[360px]:size-2",
                      i > 0 && "-ml-1", Z[i], CATEGORY_STYLE[categoryOf(t.tone)].circle, t.done && "opacity-45",
                    )}
                  />
                ))}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Quick add: owns its own text so typing never re-renders the grid ─────────
export function QuickAdd({ dateLabel, disabledReason, onAdd }: {
  dateLabel: string;
  disabledReason?: string | undefined;
  onAdd: (title: string, category: TodoCategory) => Promise<void>;
}) {
  const [text, setText] = React.useState("");
  const [category, setCategory] = React.useState<TodoCategory>("work");
  const inputRef = React.useRef<HTMLInputElement>(null);
  const clean = cleanQuickAddTitle(text);
  const nextCategory = () => CATEGORY_ORDER[(CATEGORY_ORDER.indexOf(category) + 1) % CATEGORY_ORDER.length]!;

  async function submit() {
    if (!clean || disabledReason) return;
    try {
      await onAdd(clean, category);
      setText("");
    } catch { /* the store rolled back and a toast was shown; keep the typed text */ }
    inputRef.current?.focus();
  }

  return (
    <div className="mt-2 flex items-center gap-2">
      <button
        type="button"
        aria-label={`Category: ${CATEGORY_LABEL[category]}. Click to change`}
        title={`Category: ${CATEGORY_LABEL[category]}`}
        onClick={() => setCategory(nextCategory())}
        className={cn("flex size-[34px] shrink-0 items-center justify-center rounded-[9px] border border-surface-200 bg-surface-0 hover:bg-surface-100", FOCUS)}
      >
        <i className={cn("size-3 rounded-full", CATEGORY_STYLE[category].dot)} />
      </button>
      <Input
        ref={inputRef}
        aria-label="New todo"
        value={text}
        maxLength={120}
        disabled={!!disabledReason}
        placeholder={disabledReason ?? `Add a todo for ${dateLabel}…`}
        onChange={(e) => setText(e.target.value.replace(/[\r\n]+/g, " "))}
        onPaste={(e) => { if (/[\r\n]/.test(e.clipboardData.getData("text"))) e.preventDefault(); }}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void submit(); } }}
        className="h-[34px] min-w-0 flex-1 text-[13px]"
      />
      <Button type="button" size="icon" aria-label="Add todo" disabled={!clean || !!disabledReason} onClick={() => void submit()} className="size-[34px] shrink-0">
        <Plus size={16} />
      </Button>
    </div>
  );
}
